import { useEffect, useState } from "react";
import { API, type OrderItem, type ProductInOrder, type SAVSummary } from "../api";

const COMMON_ISSUES = [
  "Ne s'allume pas du tout",
  "Ne chauffe plus",
  "Fuite d'eau",
  "Bruit anormal",
  "Code erreur affiché",
  "Problème de température",
];

type Step =
  | { kind: "loading" }
  | { kind: "pick-order" }
  | { kind: "pick-product"; order: OrderItem }
  | { kind: "describe"; order: OrderItem; product: ProductInOrder }
  | { kind: "submitting"; order: OrderItem; product: ProductInOrder }
  | { kind: "no-orders" };

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function formatEuro(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(n);
}

export function NewSAV({ onCreated }: { onCreated: (s: SAVSummary) => void }) {
  const [step, setStep] = useState<Step>({ kind: "loading" });
  const [orders, setOrders] = useState<OrderItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [description, setDescription] = useState("");

  useEffect(() => {
    API.getPrestashopOrders()
      .then((r) => {
        if (!r.prestashop_enabled || r.orders.length === 0) {
          setStep({ kind: "no-orders" });
        } else {
          setOrders(r.orders);
          setStep({ kind: "pick-order" });
        }
      })
      .catch((e) => {
        setError((e as Error).message);
        setStep({ kind: "no-orders" });
      });
  }, []);

  async function submit() {
    if (step.kind !== "describe") return;
    if (description.trim().length < 3) {
      setError("Décrivez le problème en quelques mots");
      return;
    }
    setError(null);
    setStep({ kind: "submitting", order: step.order, product: step.product });
    try {
      const sav = await API.createSAV({
        prestashop_order_id: step.order.id_order,
        prestashop_product_id: step.product.product_id,
        panne_description: description.trim(),
      });
      onCreated(sav);
    } catch (e) {
      setError((e as Error).message);
      setStep({ kind: "describe", order: step.order, product: step.product });
    }
  }

  if (step.kind === "loading") {
    return (
      <div className="xs-card" style={{ textAlign: "center", color: "#64748b" }}>
        Chargement de vos commandes…
      </div>
    );
  }

  if (step.kind === "no-orders") {
    return (
      <div className="xs-card" style={{ maxWidth: 560 }}>
        <h2>Aucune commande récente</h2>
        <p className="xs-card-desc">
          Nous ne trouvons pas de commande associée à votre compte. Pour toute
          demande SAV, contactez-nous à{" "}
          <a href="mailto:sav@fourniresto.com">sav@fourniresto.com</a>.
        </p>
        {error && <div className="xs-alert xs-alert-error">{error}</div>}
      </div>
    );
  }

  if (step.kind === "pick-order") {
    return (
      <div>
        <h2 style={{ margin: "0 0 4px 0", fontSize: 18 }}>
          Quelle commande est concernée ?
        </h2>
        <p className="xs-muted" style={{ margin: "0 0 16px 0" }}>
          Sélectionnez la commande qui contient l&apos;appareil en panne.
        </p>
        <div className="xs-list">
          {orders.map((o) => (
            <button
              key={o.id_order}
              className="xs-tile"
              onClick={() => setStep({ kind: "pick-product", order: o })}
            >
              <div className="xs-tile-row">
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="xs-tile-title">
                    {o.reference ? `Commande ${o.reference}` : `Commande #${o.id_order}`}
                  </div>
                  <div className="xs-tile-meta">
                    {formatDate(o.invoice_date || o.date_add)} ·{" "}
                    {o.products.length} produit{o.products.length > 1 ? "s" : ""}
                  </div>
                </div>
                <div className="xs-mono" style={{ fontSize: 13 }}>
                  {formatEuro(o.total_paid_tax_excl)}
                </div>
              </div>
              <div
                className="xs-tile-meta xs-truncate"
                style={{ marginTop: 4 }}
              >
                {o.products
                  .slice(0, 2)
                  .map((p) => p.product_name)
                  .filter(Boolean)
                  .join(" · ")}
                {o.products.length > 2 && ` +${o.products.length - 2}`}
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (step.kind === "pick-product") {
    const { order } = step;
    return (
      <div>
        <div className="xs-row-between" style={{ marginBottom: 12 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18 }}>Quel appareil est en panne ?</h2>
            <p className="xs-muted" style={{ margin: "4px 0 0" }}>
              Commande {order.reference || `#${order.id_order}`} du{" "}
              {formatDate(order.invoice_date || order.date_add)}
            </p>
          </div>
          <button
            className="xs-btn xs-btn-secondary xs-btn-sm"
            onClick={() => setStep({ kind: "pick-order" })}
          >
            ← Changer
          </button>
        </div>
        <div className="xs-list">
          {order.products.map((p) => (
            <button
              key={p.product_id}
              className="xs-tile"
              onClick={() => setStep({ kind: "describe", order, product: p })}
            >
              <div className="xs-tile-row">
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="xs-tile-title xs-truncate">
                    {p.product_name || "Produit"}
                  </div>
                  <div className="xs-tile-meta xs-mono">
                    {p.product_reference || "—"}
                  </div>
                </div>
                <div
                  className="xs-tile-meta"
                  style={{ textAlign: "right", flexShrink: 0 }}
                >
                  {formatEuro(p.unit_price_tax_excl)} HT
                  {p.weight_kg !== null && <div>{p.weight_kg} kg</div>}
                  {p.age_months !== null && <div>il y a {p.age_months} mois</div>}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // describe / submitting
  const product = step.product;
  const order = step.order;
  const isSubmitting = step.kind === "submitting";

  return (
    <div>
      <div className="xs-row-between" style={{ marginBottom: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18 }}>Que se passe-t-il ?</h2>
          <p className="xs-muted" style={{ margin: "4px 0 0" }}>
            Décrivez le problème — Xsavier s&apos;occupe du reste.
          </p>
        </div>
        <button
          className="xs-btn xs-btn-secondary xs-btn-sm"
          disabled={isSubmitting}
          onClick={() => setStep({ kind: "pick-product", order })}
        >
          ← Changer
        </button>
      </div>

      <div className="xs-card xs-card-sub" style={{ marginBottom: 16 }}>
        <div className="xs-muted" style={{ fontSize: 11, marginBottom: 4 }}>
          Appareil sélectionné
        </div>
        <div style={{ fontWeight: 600 }}>{product.product_name || "—"}</div>
        <div className="xs-muted xs-mono" style={{ marginTop: 4 }}>
          Réf {product.product_reference || "—"} ·{" "}
          {formatEuro(product.unit_price_tax_excl)} HT
          {product.weight_kg !== null && ` · ${product.weight_kg} kg`}
        </div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <div className="xs-muted" style={{ fontSize: 11, marginBottom: 6 }}>
          Symptômes courants
        </div>
        <div className="xs-chips">
          {COMMON_ISSUES.map((i) => (
            <button
              key={i}
              type="button"
              className="xs-chip"
              onClick={() =>
                setDescription((d) => (d ? d + " · " : "") + i)
              }
            >
              {i}
            </button>
          ))}
        </div>
      </div>

      <label className="xs-field">
        <span className="xs-field-label">Description du problème</span>
        <textarea
          className="xs-textarea"
          rows={4}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Ex: Mon four ne chauffe plus depuis ce matin, j'ai un service dans 2h…"
          disabled={isSubmitting}
          autoFocus
        />
      </label>

      {error && <div className="xs-alert xs-alert-error">{error}</div>}

      <button
        className="xs-btn xs-btn-primary"
        onClick={submit}
        disabled={isSubmitting || description.trim().length < 3}
      >
        {isSubmitting ? "Envoi…" : "Envoyer ma demande SAV"}
      </button>
    </div>
  );
}
